// Overlap mini-map — "an overlap mini-map of the viewer's entitled
// footprints" (design §6.3). Reuses the SAME atlas-projector utilities
// PortfolioAtlas.tsx already relies on (lib/whitespace.ts's makeAtlasProjector
// + computeCentroid2D), so this view and the Atlas can never disagree about
// where a project's documents sit. Draws one soft-filled ellipse per member
// project from the real /whitespace/portfolio_map layout, filtered to this
// group's members — never a fabricated shape when a project has fewer than
// 2 plotted points (computeFootprintEllipse's own honesty rule).
import {
  computeGroupFootprints,
  computeFootprintBounds,
  makeAtlasProjector,
  projectTagColor,
} from '../../lib/whitespace'
import type { PortfolioMapLayout } from '../../types/whitespace'

interface OverlapMiniMapProps {
  layout: PortfolioMapLayout | null
  memberProjectIds: string[]
  projectNames: Map<string, string>
}

const VIEWPORT = { width: 300, height: 190, padding: 20 }

export default function OverlapMiniMap({ layout, memberProjectIds, projectNames }: OverlapMiniMapProps) {
  if (!layout) {
    return (
      <div className="light-card p-3 text-center" data-testid="overlap-minimap-no-layout">
        <p className="text-[11px] text-gray-500">
          No portfolio map yet — the mini-map needs the Portfolio Atlas's 2-D layout to have run at least once.
        </p>
      </div>
    )
  }

  const ellipses = computeGroupFootprints(layout, memberProjectIds)

  if (ellipses.length === 0) {
    return (
      <div className="light-card p-3 text-center" data-testid="overlap-minimap-empty">
        <p className="text-[11px] text-gray-500">
          Not enough plotted documents yet to draw footprints for this group.
        </p>
      </div>
    )
  }

  const bounds = computeFootprintBounds(ellipses)
  const project = makeAtlasProjector(bounds, VIEWPORT)

  return (
    <div className="light-card p-2" data-testid="overlap-minimap">
      <svg width="100%" height={VIEWPORT.height} viewBox={`0 0 ${VIEWPORT.width} ${VIEWPORT.height}`} role="img" aria-label="Entitled footprints for this group's member projects">
        {ellipses.map((e) => {
          const [cx, cy] = project([e.cx, e.cy])
          const [ex, ey] = project([e.cx + e.rx, e.cy + e.ry])
          const rx = Math.abs(ex - cx)
          const ry = Math.abs(ey - cy)
          const color = projectTagColor(e.project_id, memberProjectIds)
          return (
            <ellipse
              key={e.project_id}
              cx={cx}
              cy={cy}
              rx={rx}
              ry={ry}
              fill={`${color}1A`}
              stroke={color}
              strokeWidth={2}
            />
          )
        })}
      </svg>
      <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center mt-1">
        {ellipses.map((e) => (
          <span key={e.project_id} className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: projectTagColor(e.project_id, memberProjectIds) }}>
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: projectTagColor(e.project_id, memberProjectIds) }} aria-hidden />
            {projectNames.get(e.project_id) ?? e.project_id}
          </span>
        ))}
      </div>
      <p className="text-center text-[10px] text-gray-500 mt-1">
        Footprints and shared ground — a simplified 2-D reading
      </p>
    </div>
  )
}
